import React, { useState, useEffect } from 'react';
import { confirmSignUp, resendSignUpCode } from '@aws-amplify/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  background-color: var(--surface);
`;

const HeroSection = styled.div`
  position: relative;
  min-height: 600px;
  background: url('/images/hero-2.jpeg') center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: center;
    padding: 0 var(--spacing-md);
    min-height: 100vh;
    text-align: center;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0.6) 50%,
      rgba(0, 0, 0, 0.4) 100%
    );
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 600px;
  color: white;
  
  h1 {
    font-size: 3.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    line-height: 1.2;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
      margin-bottom: 1rem;
    }
    
    @media (max-width: 480px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    opacity: 0.9;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
      margin-bottom: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1rem;
    }
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin-bottom: var(--spacing-xl);
  }
`;

const ConfirmCard = styled.div`
  position: relative;
  z-index: 2;
  width: 400px;
  background: rgba(255, 255, 255, 0.95);
  padding: var(--spacing-xl);
  border-radius: var(--radius-lg);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 400px;
    padding: var(--spacing-lg);
  }
  
  @media (max-width: 480px) {
    padding: var(--spacing-md);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const Input = styled.input`
  width: 100%;
  padding: var(--spacing-sm);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.1);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: var(--spacing-sm);
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: var(--primary-dark);
  }
`;

const ErrorMessage = styled.div`
  padding: var(--spacing-sm);
  background: var(--error);
  color: white;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
`;

const InfoText = styled.p`
  color: var(--text-secondary);
  text-align: center;
  font-size: var(--font-size-sm);
  margin-bottom: var(--spacing-md);
`;

const SecondaryButton = styled.button`
  flex: 1;
  padding: var(--spacing-sm);
  background: transparent;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: var(--primary-color);
    color: white;
  }
`;

const SuccessMessage = styled.div`
  padding: var(--spacing-sm);
  background: var(--success);
  color: white;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
`;

const ConfirmSignUp = ({ username, onConfirmation }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check for success message from navigation state
  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear the state to prevent showing the message again on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  const handleConfirmation = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      await confirmSignUp({ username, confirmationCode: code });
      onConfirmation();
      navigate('/signin');
    } catch (error) {
      console.error('Confirm sign up error:', error.message);
      setError(error.message);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError('');
    setSuccess('');
    
    try {
      console.log('Attempting to resend verification code for user:', username);
      
      await resendSignUpCode({ username });
      console.log('Verification code resent successfully');
      setSuccess('Verification code has been resent to your email!');
    } catch (error) {
      console.error('Failed to resend verification code:', error.message);
      setError(`Failed to resend code: ${error.message}`);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Container>
      <HeroSection>
        <HeroContent>
          <h1>Empowering Young Minds Through Continuous Learning</h1>
          <p>Join thousands of students achieving their academic goals with BrainyHoot's 360° learning experience.</p>
        </HeroContent>
        
        <ConfirmCard>
          <h2 style={{ marginBottom: 'var(--spacing-lg)', textAlign: 'center' }}>Verify Your Email</h2>
          <Form onSubmit={handleConfirmation}>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}
            
            <InfoText>
              We've sent a verification code to <strong>{username}</strong>. Verify spam folder as well.
            </InfoText>
            
            <Input
              type="text"
              placeholder="Enter verification code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
            
            <Button type="submit">
              Verify & Complete Sign Up
            </Button>
            
            <SecondaryButton 
              type="button" 
              onClick={handleResendCode}
              disabled={isResending}
              style={{ width: '100%' }}
            >
              {isResending ? 'Sending...' : 'Resend Code'}
            </SecondaryButton>
          </Form>
        </ConfirmCard>
      </HeroSection>

      {/* Features Section */}
      <div className="container">
        <h2 style={{ textAlign: 'center', marginBottom: 'var(--spacing-2xl)' }}>Why Choose BrainyHoot?</h2>
        <div className="grid" style={{ gap: 'var(--spacing-xl)' }}>
          <div className="card">
            <h3 style={{ color: 'var(--primary-color)', marginBottom: 'var(--spacing-md)' }}>
              Comprehensive Assessments
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Access a wide range of carefully curated questions designed to enhance your real world problem-solving skills.
            </p>
          </div>
          <div className="card">
            <h3 style={{ color: 'var(--primary-color)', marginBottom: 'var(--spacing-md)' }}>
              Continuous Growth
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Gain insights from every assessment to drive continuous learning and long-term growth.
            </p>
          </div>
          <div className="card">
            <h3 style={{ color: 'var(--primary-color)', marginBottom: 'var(--spacing-md)' }}>
              Expert Support
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Our team of experienced educators is here to guide you through your learning journey.
            </p>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div style={{ 
        backgroundColor: 'white',
        padding: 'var(--spacing-2xl) 0',
        marginTop: 'var(--spacing-2xl)'
      }}>
        <div className="container">
          <div style={{ 
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: 'var(--spacing-xl)' }}>About BrainyHoot</h2>
            <p style={{ 
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
              fontSize: 'var(--font-size-lg)',
              marginBottom: 'var(--spacing-xl)'
            }}>
              At BrainyHoot, we're transforming the way students learn—combining technology, innovation, and expert guidance to support continuous, meaningful education.
              Our mission goes beyond exam preparation. We aim to nurture a deep understanding of concepts and their real-world applications, empowering students to learn for life, not just for exams.
              <br /><br />
              Quality learning experiences, accessible to all—that's our promise.
            </p>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 'var(--spacing-xl)',
              textAlign: 'center'
            }}>
              <div>
                <div style={{ 
                  fontSize: '2rem', 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  🎯
                </div>
                <h3 style={{ 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-xs)',
                  fontSize: 'var(--font-size-lg)'
                }}>
                  Personalized Learning
                </h3>
                <p style={{ 
                  color: 'var(--text-secondary)',
                  fontSize: 'var(--font-size-md)'
                }}>
                  Adaptive tests tailored to your learning pace
                </p>
              </div>
              <div>
                <div style={{ 
                  fontSize: '2rem', 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  💡
                </div>
                <h3 style={{ 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-xs)',
                  fontSize: 'var(--font-size-lg)'
                }}>
                  Continuous Growth
                </h3>
                <p style={{ 
                  color: 'var(--text-secondary)',
                  fontSize: 'var(--font-size-md)'
                }}>
                  Regular updates with new content and features
                </p>
              </div>
              <div>
                <div style={{ 
                  fontSize: '2rem', 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  🤝
                </div>
                <h3 style={{ 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-xs)',
                  fontSize: 'var(--font-size-lg)'
                }}>
                  Expert Support
                </h3>
                <p style={{ 
                  color: 'var(--text-secondary)',
                  fontSize: 'var(--font-size-md)'
                }}>
                  Dedicated team to assist your learning journey
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default ConfirmSignUp;
